import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const CLASSIFICATIONS = ["UNCLASSIFIED", "CONFIDENTIAL", "SECRET", "OMEGA"];
const STATUSES = ["ACTIVE", "REDACTED", "LOST"];

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function RealityDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [item, setItem] = useState(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/realities/${id}`).then((r) => setItem(r.data)).catch(() => setItem(false));
  }, [id]);

  const startEdit = () => {
    setDraft({ ...item });
    setEditing(true);
  };
  const cancel = () => { setEditing(false); setDraft(null); };
  const save = async () => {
    setSaving(true);
    try {
      const res = await api.put(`/realities/${id}`, draft);
      setItem(res.data);
      setEditing(false);
      toast.success("Reality sealed.");
    } catch (e) {
      toast.error("Update failed.");
    } finally {
      setSaving(false);
    }
  };
  const remove = async () => {
    if (!window.confirm("Sever this reality from the catalogue?")) return;
    try {
      await api.delete(`/realities/${id}`);
      toast.success("Reality severed.");
      navigate("/realities");
    } catch (e) {
      toast.error("Could not sever.");
    }
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 1.2 * 1024 * 1024) {
      toast.error("Image too large (max ~1.2MB).");
      return;
    }
    const b64 = await fileToBase64(file);
    setDraft((d) => ({ ...d, images: [...(d.images || []), b64] }));
  };

  const removeImage = (i) => {
    setDraft((d) => ({ ...d, images: d.images.filter((_, idx) => idx !== i) }));
  };

  if (item === null)
    return <div className="text-[#555] text-xs tracking-widest uppercase">::: retrieving record :::</div>;
  if (item === false)
    return (
      <div className="border border-[#333] p-12 text-center">
        <div className="font-serif-display text-3xl mb-4">Reality not catalogued.</div>
        <Link to="/realities" className="tiv-btn">Return to catalogue</Link>
      </div>
    );

  const v = editing ? draft : item;

  return (
    <div className="space-y-10" data-testid="reality-detail-page">
      <div className="text-[0.65rem] tracking-[0.35em] uppercase text-[#A3A3A3]">
        <Link to="/realities" className="hover:text-[#E3E1D9]">↖ Realities</Link>
        <span className="mx-3 text-[#555]">/</span>
        <span>{item.id}</span>
      </div>

      <header className="grid grid-cols-1 lg:grid-cols-12 gap-8 border-b border-[#333] pb-10">
        <div className="lg:col-span-8 space-y-5">
          <div className="flex items-center gap-3">
            <span className="text-xs tracking-[0.3em] text-[#A3A3A3]">REALITY</span>
            <span className="text-sm tracking-[0.3em] text-[#E3E1D9]">{v.designation || item.id}</span>
            <span className={`status-pill ${
              v.status === "ACTIVE" ? "status-active" :
              v.status === "REDACTED" ? "status-redacted" : "status-lost"
            }`}>{v.status}</span>
          </div>
          {editing ? (
            <>
              <input
                className="tiv-input text-3xl"
                value={draft.title}
                onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                placeholder="title..."
                data-testid="reality-edit-title"
              />
              <input
                className="tiv-input"
                value={draft.designation}
                onChange={(e) => setDraft({ ...draft, designation: e.target.value })}
                placeholder="designation (e.g. R-γ)"
                data-testid="reality-edit-designation"
              />
            </>
          ) : (
            <h1 className="font-serif-display text-4xl sm:text-5xl leading-tight tracking-tighter">
              {item.title || <span className="text-[#555] italic">[ untitled ]</span>}
            </h1>
          )}
        </div>
        <aside className="lg:col-span-4 border border-[#333] p-5 space-y-3 text-xs font-mono-data bg-[#0a0a0a]">
          <div className="section-label">
            <span>Index Card</span>
            <span>{item.id}</span>
          </div>
          {editing ? (
            <div className="space-y-3">
              <label className="block">
                <div className="text-[0.6rem] tracking-[0.3em] uppercase text-[#555] mb-1">Classification</div>
                <select className="tiv-input" value={draft.classification} onChange={(e) => setDraft({ ...draft, classification: e.target.value })} data-testid="reality-edit-classification">
                  {CLASSIFICATIONS.map((c) => <option key={c} value={c} className="bg-[#0a0a0a]">{c}</option>)}
                </select>
              </label>
              <label className="block">
                <div className="text-[0.6rem] tracking-[0.3em] uppercase text-[#555] mb-1">Status</div>
                <select className="tiv-input" value={draft.status} onChange={(e) => setDraft({ ...draft, status: e.target.value })} data-testid="reality-edit-status">
                  {STATUSES.map((c) => <option key={c} value={c} className="bg-[#0a0a0a]">{c}</option>)}
                </select>
              </label>
              <label className="block">
                <div className="text-[0.6rem] tracking-[0.3em] uppercase text-[#555] mb-1">Date</div>
                <input className="tiv-input" value={draft.date} onChange={(e) => setDraft({ ...draft, date: e.target.value })} data-testid="reality-edit-date" />
              </label>
            </div>
          ) : (
            <div className="space-y-2 text-[#A3A3A3]">
              <div className="flex justify-between"><span>Class</span><span className="text-[#E3E1D9]">{item.classification}</span></div>
              <div className="flex justify-between"><span>Status</span><span className="text-[#E3E1D9]">{item.status}</span></div>
              <div className="flex justify-between"><span>Date</span><span className="text-[#E3E1D9]">{item.date || "—"}</span></div>
              <div className="flex justify-between"><span>Updated</span><span className="text-[#E3E1D9]">{item.updated_at?.split("T")[0]}</span></div>
            </div>
          )}
        </aside>
      </header>

      <section>
        <div className="section-label"><span>Observation · Body</span><span>§ folio</span></div>
        {editing ? (
          <textarea className="tiv-textarea" value={draft.body} onChange={(e) => setDraft({ ...draft, body: e.target.value })} placeholder="describe this reality..." data-testid="reality-edit-body" />
        ) : item.body ? (
          <div className="text-base leading-loose whitespace-pre-wrap max-w-3xl">{item.body}</div>
        ) : (
          <div className="border border-dashed border-[#333] p-10 text-center text-[#555] text-xs tracking-widest uppercase">::: no observation recorded :::</div>
        )}
      </section>

      <section>
        <div className="section-label"><span>Plates</span><span>{(v.images || []).length}</span></div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {(v.images || []).map((src, i) => (
            <div key={`${src.slice(-24)}-${i}`} className="border border-[#333] aspect-square overflow-hidden relative group">
              <img src={src} alt={`plate-${i}`} className="w-full h-full object-cover tiv-img" />
              {editing && (
                <button onClick={() => removeImage(i)} className="absolute top-1 right-1 tiv-btn tiv-btn-danger text-[0.55rem] px-2 py-1 opacity-0 group-hover:opacity-100">Burn</button>
              )}
            </div>
          ))}
          {editing && (
            <label className="border border-dashed border-[#333] aspect-square flex items-center justify-center cursor-crosshair hover:border-[#E3E1D9]">
              <input type="file" accept="image/*" className="hidden" onChange={onUpload} />
              <span className="text-xs tracking-[0.3em] uppercase text-[#A3A3A3]">+ Affix Plate</span>
            </label>
          )}
        </div>
      </section>

      {isAdmin && (
        <div className="flex flex-wrap gap-3 border-t border-[#333] pt-8">
          {editing ? (
            <>
              <button className="tiv-btn" onClick={save} disabled={saving} data-testid="reality-save-btn">{saving ? "Sealing..." : "Seal & Save"}</button>
              <button className="tiv-btn" onClick={cancel} data-testid="reality-cancel-btn">Discard</button>
            </>
          ) : (
            <>
              <button className="tiv-btn" onClick={startEdit} data-testid="reality-edit-btn">Edit Record</button>
              <button className="tiv-btn tiv-btn-danger" onClick={remove} data-testid="reality-delete-btn">Sever</button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
